import type { AGUIEvent } from '@ag-ui/core'

export const DMP_CHAT_SIGNALR_TRACE_MAX_MESSAGES = 5_000

const DMP_CHAT_SIGNALR_TRACE_FILE_VERSION = 1 as const

export type DmpChatSignalRTraceParseStatus = 'parsed' | 'ignored'

export interface DmpChatSignalRTraceEntry {
  receivedAt: string
  rawPayload: string
  parseStatus: DmpChatSignalRTraceParseStatus
  parsedEvent: AGUIEvent | null
}

export interface DmpChatSignalRTraceFile {
  formatVersion: typeof DMP_CHAT_SIGNALR_TRACE_FILE_VERSION
  capturedAt: string
  messages: readonly DmpChatSignalRTraceEntry[]
}

export interface DmpChatSignalRTraceRecorder {
  record: (rawPayload: string, parsedEvent: AGUIEvent | null) => number
  getEntries: () => readonly DmpChatSignalRTraceEntry[]
  clear: () => void
}

export function createDmpChatSignalRTraceRecorder(enabled: boolean): DmpChatSignalRTraceRecorder {
  const entries: DmpChatSignalRTraceEntry[] = []

  return {
    record: (rawPayload, parsedEvent) => {
      if (!enabled) {
        return 0
      }

      entries.push({
        receivedAt: new Date().toISOString(),
        rawPayload,
        parseStatus: parsedEvent ? 'parsed' : 'ignored',
        parsedEvent,
      })

      if (entries.length > DMP_CHAT_SIGNALR_TRACE_MAX_MESSAGES) {
        entries.splice(0, entries.length - DMP_CHAT_SIGNALR_TRACE_MAX_MESSAGES)
      }

      return entries.length
    },
    getEntries: () => entries.slice(),
    clear: () => {
      entries.length = 0
    },
  }
}

export function downloadDmpChatSignalRTrace(entries: readonly DmpChatSignalRTraceEntry[]): void {
  if (entries.length === 0) {
    return
  }

  const traceFile: DmpChatSignalRTraceFile = {
    formatVersion: DMP_CHAT_SIGNALR_TRACE_FILE_VERSION,
    capturedAt: new Date().toISOString(),
    messages: entries,
  }
  const blob = new Blob([JSON.stringify(traceFile, null, 2)], { type: 'application/json' })
  const objectUrl = URL.createObjectURL(blob)
  const link = document.createElement('a')

  link.href = objectUrl
  link.download = `dmp-chat-signalr-trace-${formatTraceFileTimestamp(new Date())}.json`
  document.body.appendChild(link)
  link.click()
  link.remove()
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 0)
}

function formatTraceFileTimestamp(date: Date): string {
  return date.toISOString().replace(/[:.]/g, '-')
}
